import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { jsonBody, successResponse, tagPaths } from "../util.ts";
import { datetime_epoch_s } from "./common.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

export const CampaignsType = z
    .enum(["all_users", "new_users"])
    .describe(
        "Campaign target audience. `new_users` applies to first-time users; `all_users` is not restricted to new signups.",
    )
    .openapi({ ref: "CampaignsType" });

const include_deleted_query = z
    .boolean()
    .optional()
    .openapi({ param: { name: "include_deleted", in: "query" } });

//#region Campaigns
export const Campaigns = z
    .object({
        campaign_code: z
            .string()
            .describe("Promo code entered by the user or used internally to identify the campaign."),
        campaign_description: z.string().describe("Internal free-form campaign description."),
        campaign_type: CampaignsType,
        created_at: datetime_epoch_s,
        deleted_at: datetime_epoch_s.optional(),
        credit_amount: z
            .number()
            .describe(
                "Credit amount granted by the campaign on eligible first top-up or first billing account creation.",
            ),
        id: z.number(),
        is_active: z.boolean().describe("Whether the campaign is currently active and can be used."),
        is_deleted: z.boolean().describe("Whether the campaign has been deleted."),
        used_count: z.number().describe("How many times the campaign has been used."),
    })
    .openapi({ ref: "Campaigns" });

export const CampaignsCreateBody = z
    .object({
        campaign_code: z.string().describe("Free-form promo code for the campaign."),
        campaign_description: z.string().describe("Internal campaign description, not shown to end users."),
        campaign_type: CampaignsType,
        credit_amount: z.number().describe("Credit amount granted by the campaign."),
        is_active: z.boolean().describe("Whether the campaign should be active immediately after creation."),
    })
    .openapi({ ref: "CampaignsCreateBody" });

export const CampaignsUpdateBody = z
    .object({
        campaign_description: z.string().describe("Updated internal campaign description."),
        is_active: z.boolean().describe("Updated campaign active state."),
    })
    .openapi({ ref: "CampaignsUpdateBody" });

export const CampaignsResponse = z.array(Campaigns);

//#endregion

export const campaignsPaths = tagPaths("admin_campaigns")({
    "/payment/admin/campaign/list": {
        get: {
            summary: "List campaigns",
            description: "List existing campaigns. Set `include_deleted=true` to also include deleted campaigns.",
            parameters: [include_deleted_query],
            responses: { ...successResponse(CampaignsResponse) },
        },
    },
    "/payment/admin/campaign": {
        post: {
            summary: "Create campaign",
            description: "Create a campaign that can grant signup or first-topup credit to eligible billing accounts.",
            requestBody: jsonBody(CampaignsCreateBody),
            responses: { ...successResponse(Campaigns) },
        },
    },
    "/payment/admin/campaign/{id}": {
        put: {
            summary: "Update campaign",
            description:
                "Update a campaign's description or active state. Campaign code and credit amount cannot be changed.",
            parameters: [params.id],
            requestBody: jsonBody(CampaignsUpdateBody),
            responses: { ...successResponse(Campaigns) },
        },
        delete: {
            summary: "Delete campaign",
            description:
                "Delete a campaign without changing billing accounts that were previously created or credited with that code.",
            parameters: [params.id],
            responses: { ...successResponse(Campaigns) },
        },
    },
});
